import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="region-search"
export default class extends Controller {
  static targets = ["input", "results"]

  connect() {
    this.resultsTarget.classList.add("hidden")
    this.debounceTimer = null
    this.debounceDelay = 300

    document.addEventListener('click', (event) => {
      if (!this.element.contains(event.target) && !this.resultsTarget.classList.contains('hidden')) {
        this.resultsTarget.classList.add('hidden')
      }
    })
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.resultsTarget.classList.add("hidden")
      return
    }

    clearTimeout(this.debounceTimer)

    this.debounceTimer = setTimeout(() => {
      fetch(`/api/v1/regions/search?query=${encodeURIComponent(query)}`)
        .then(response => response.json())
        .then(data => {
          this.renderResults(data)
        })
        .catch(error => console.error("Error fetching regions:", error))
    }, this.debounceDelay)
  }

  renderResults(data) {
    if (!data || (data.regions.length === 0 && data.hotels.length === 0)) {
      this.resultsTarget.classList.add("hidden")
      return
    }

    this.resultsTarget.innerHTML = ""

    // Render Regions
    if(data.regions.length > 0) {
      data.regions.forEach(region => {
        const regionItem = document.createElement("div")
        regionItem.className = "hover:bg-[#d4d4d8] cursor-pointer text-black flex h-16 justify-start items-center gap-x-2 rounded-small px-2"
        regionItem.dataset.regionId = region.id
        regionItem.dataset.regionSlug = region.slug
        regionItem.dataset.resultType = "region"
        regionItem.dataset.action = "click->region-search#selectRegion click->search-form#disableSearchButton"
  
        const nameInfoRegion = document.createElement("div")
        nameInfoRegion.className = "flex flex-col justify-center h-full"

        const regionName = document.createElement("span")
        regionName.className = "font-bold text-base region-name line-clamp-1"
        regionName.textContent = region.name
  
        const regionInfo = document.createElement("span")
        regionInfo.textContent = region.info_name
        regionInfo.className = "text-gray-500 text-[14px] region-info line-clamp-1"

        nameInfoRegion.appendChild(regionName)
        nameInfoRegion.appendChild(regionInfo)

        const regionIcon = document.createElement("span")
        regionIcon.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2.25C8.27344 2.25 5.25 5.12766 5.25 8.67188C5.25 12.75 9.75 19.2127 11.4023 21.4448C11.4709 21.5391 11.5608 21.6157 11.6647 21.6686C11.7686 21.7215 11.8835 21.749 12 21.749C12.1165 21.749 12.2314 21.7215 12.3353 21.6686C12.4392 21.6157 12.5291 21.5391 12.5977 21.4448C14.25 19.2136 18.75 12.7533 18.75 8.67188C18.75 5.12766 15.7266 2.25 12 2.25Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 11.25C13.2426 11.25 14.25 10.2426 14.25 9C14.25 7.75736 13.2426 6.75 12 6.75C10.7574 6.75 9.75 7.75736 9.75 9C9.75 10.2426 10.7574 11.25 12 11.25Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `
  
        regionItem.appendChild(regionIcon)
        regionItem.appendChild(nameInfoRegion)
        
        this.resultsTarget.appendChild(regionItem)
      })
    }

    if(data.hotels.length > 0) {
      data.hotels.forEach(hotel => {
        const hotelItem = document.createElement("div")
        hotelItem.className = "hover:bg-[#d4d4d8] cursor-pointer text-black flex h-16 justify-start items-center gap-x-2 rounded-small px-2"
        hotelItem.dataset.hotelId = hotel.id
        hotelItem.dataset.hotelSlug = hotel.slug
        hotelItem.dataset.regionSlug = hotel.region_slug
        hotelItem.dataset.resultType = "hotel"
        hotelItem.dataset.action = "click->region-search#selectHotel click->search-form#disableSearchButton"

        const nameInfoHotel = document.createElement("div")
        nameInfoHotel.className = "flex flex-col justify-center h-full"

        const hotelName = document.createElement("span")
        hotelName.className = "font-bold text-base hotel-name line-clamp-1"
        hotelName.textContent = hotel.name

        const hotelInfo = document.createElement("span")
        hotelInfo.textContent = hotel.info_name
        hotelInfo.className = "text-gray-500 text-[14px] hotel-info line-clamp-1"

        nameInfoHotel.appendChild(hotelName)
        nameInfoHotel.appendChild(hotelInfo)

        const hotelIcon = document.createElement("span")
        hotelIcon.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 2H22" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M19 2H5C4.44772 2 4 2.44772 4 3V21C4 21.5523 4.44772 22 5 22H19C19.5523 22 20 21.5523 20 21V3C20 2.44772 19.5523 2 19 2Z" stroke="black" stroke-width="2" stroke-linejoin="round"/>
            <path d="M10 16H14V22H10V16Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M7.5 6H8.5M7.5 9H8.5M11.5 6H12.5M11.5 9H12.5M15.5 6H16.5M15.5 9H16.5" stroke="black" stroke-width="2" stroke-linecap="round"/>
            <path d="M2 22H22M14 16H15C15.276 16 15.505 15.774 15.452 15.503C15.176 14.083 13.7355 13 12 13C10.2645 13 8.824 14.0825 8.548 15.503C8.495 15.774 8.724 16 9 16H10" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `
        hotelItem.appendChild(hotelIcon)
        hotelItem.appendChild(nameInfoHotel)
        
        this.resultsTarget.appendChild(hotelItem)
      })
    }

    this.resultsTarget.classList.remove("hidden")
  }

  selectRegion(event) {
    const regionId = event.currentTarget.dataset.regionId
    const regionName = event.currentTarget.querySelector(".region-name").textContent
    const regionInfo = event.currentTarget.querySelector(".region-info").textContent
    const regionSlug = event.currentTarget.dataset.regionSlug

    this.inputTarget.value = regionInfo ? `${regionName}, ${regionInfo}` : regionName
    this.inputTarget.dataset.selectedRegionId = regionId
    this.inputTarget.dataset.selectedRegionSlug = regionSlug
    this.inputTarget.dataset.selectedResultType = "region"

    this.resultsTarget.classList.add("hidden")
  }

  selectHotel(event) {
    const hotelId = event.currentTarget.dataset.hotelId
    const hotelName = event.currentTarget.querySelector(".hotel-name").textContent
    const hotelSlug = event.currentTarget.dataset.hotelSlug
    const regionSlug = event.currentTarget.dataset.regionSlug

    this.inputTarget.value = hotelName
    this.inputTarget.dataset.selectedHotelId = hotelId
    this.inputTarget.dataset.selectedHotelSlug = hotelSlug
    this.inputTarget.dataset.selectedResultType = "hotel"
    this.inputTarget.dataset.selectedRegionSlug = regionSlug

    this.resultsTarget.classList.add("hidden")
  }
}
